import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader, Mail, Send, CheckCircle, XCircle } from 'lucide-react';

export const EmailSettingsSection = ({ API }) => {
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Kaiser Bridge Hotel',
    admin_email: '',
    notify_new_booking: true,
    notify_payment_success: true,
    notify_payment_failed: true,
    notify_booking_cancelled: true
  });
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/email-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailSettings(data);
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to fetch email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const response = await fetch(`${API}/admin/email-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(emailSettings)
      });

      if (response.ok) {
        toast.success('Email settings saved successfully!');
        await fetchEmailSettings(); // Refresh to get masked password
      } else {
        toast.error('Failed to save email settings');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error('Error saving email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    try {
      setTestingEmail(true);

      const response = await fetch(`${API}/admin/email-settings/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          smtp_host: emailSettings.smtp_host,
          smtp_port: emailSettings.smtp_port,
          smtp_username: emailSettings.smtp_username,
          smtp_password: emailSettings.smtp_password,
          test_email: testEmail
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('✅ Test email sent successfully! Check your inbox.');
      } else {
        toast.error(`❌ Test failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error testing email:', error);
      toast.error('Error sending test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading && !emailSettings.smtp_host) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center mb-3">
          <Mail className="w-6 h-6 text-green-600 mr-3" />
          <h3 className="text-xl font-semibold text-gray-900">Email Notification Settings</h3>
        </div>
        <p className="text-sm text-gray-700">
          Configure SMTP settings to receive email notifications for bookings, payments, and cancellations.
        </p>
      </div>

      <form onSubmit={handleSaveSettings} className="space-y-6">
        {/* Enable/Disable Email Notifications */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Email Notifications</h4>
                <p className="text-sm text-gray-600">Enable or disable all email notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* SMTP Configuration */}
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold text-gray-900 mb-4">SMTP Server Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtp_host">SMTP Server *</Label>
                <Input
                  id="smtp_host"
                  type="text"
                  placeholder="corfuhotelkaiser.com"
                  value={emailSettings.smtp_host}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="smtp_port">SMTP Port *</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="465"
                  value={emailSettings.smtp_port}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Use 465 for SSL or 587 for TLS</p>
              </div>

              <div>
                <Label htmlFor="smtp_username">SMTP Username *</Label>
                <Input
                  id="smtp_username"
                  type="text"
                  placeholder="reservations@corfuhotelkaiser.com"
                  value={emailSettings.smtp_username}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="smtp_password">SMTP Password *</Label>
                <Input
                  id="smtp_password"
                  type="password"
                  placeholder="Enter SMTP password"
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                  required={!emailSettings.smtp_password || emailSettings.smtp_password === '****'}
                />
                <p className="text-xs text-gray-500 mt-1">Leave as **** to keep existing password</p>
              </div>

              <div>
                <Label htmlFor="from_email">From Email *</Label>
                <Input
                  id="from_email"
                  type="email"
                  placeholder="reservations@corfuhotelkaiser.com"
                  value={emailSettings.from_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="from_name">From Name *</Label>
                <Input
                  id="from_name"
                  type="text"
                  placeholder="Kaiser Bridge Hotel"
                  value={emailSettings.from_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Recipients */}
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Notification Recipient</h4>
            <div>
              <Label htmlFor="admin_email">Admin Email (Receives Notifications) *</Label>
              <Input
                id="admin_email"
                type="email"
                placeholder="reservations@corfuhotelkaiser.com"
                value={emailSettings.admin_email}
                onChange={(e) => setEmailSettings({ ...emailSettings, admin_email: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This email will receive notifications for new bookings, payments, failures, and cancellations
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Notification Types */}
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Notification Types</h4>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.notify_new_booking}
                  onChange={(e) => setEmailSettings({ ...emailSettings, notify_new_booking: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">New Booking</span>
                  <p className="text-sm text-gray-600">Receive email when a new booking is created</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.notify_payment_success}
                  onChange={(e) => setEmailSettings({ ...emailSettings, notify_payment_success: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Payment Success</span>
                  <p className="text-sm text-gray-600">Receive email when payment is successfully received</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.notify_payment_failed}
                  onChange={(e) => setEmailSettings({ ...emailSettings, notify_payment_failed: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Payment Failed</span>
                  <p className="text-sm text-gray-600">Receive email when payment fails</p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailSettings.notify_booking_cancelled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, notify_booking_cancelled: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Booking Cancelled</span>
                  <p className="text-sm text-gray-600">Receive email when a booking is cancelled</p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Test Email Configuration</h4>
            <p className="text-sm text-gray-600 mb-4">
              Send a test email to verify your SMTP settings are working correctly.
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter test email address"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <Button
                type="button"
                onClick={handleTestEmail}
                disabled={testingEmail || !emailSettings.smtp_host}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                {testingEmail ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Email Settings
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Info Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Configuration Notes</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• All email notifications will be sent to the Admin Email address</li>
          <li>• Guest confirmation emails are sent to the customer's email</li>
          <li>• SMTP password is encrypted in the database</li>
          <li>• Use port 465 for SSL (recommended) or 587 for TLS</li>
          <li>• Test your configuration before enabling notifications</li>
        </ul>
      </div>
    </div>
  );
};
